import * as React from "react";
import {
  CreateInvestigationNotificationDetails,
  CreateInvestigationNotificationForm,
  CreateInvestigationNotificationSummary
} from "security-app/components/Investigations/notifications";
import { PaginationProvider } from "common/contexts";
import useLicenseCheck from "license/useLicenseCheck";
import SecurityPage from "security-app/SecurityPage";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import entityRoutesResolver from "security-app/routes/entityRoutesResolver";
import AssetsField from "security-app/components/Assets/plugin/AssetsField";
import ExportResultsButton from "security-app/components/SecurityEvents/Alerts/ExportResultsButton";
import securityEntityPermissionsMapper from "security-app/SecurityEntityPermissionsMapper";
import AssetEventFormContainer from "security-app/components/AssetEvents/AssetEventFormContainer";
import AssetEventForm from "security-app/components/AssetEvents/AssetEventForm";
import AssetEventSummaryContainer from "security-app/components/AssetEvents/AssetEventSummaryContainer";
import AnomalyEventFormContainer from "security-app/components/AnomalyEvents/AnomalyEventFormContainer";
import AnomalyEventForm from "security-app/components/AnomalyEvents/AnomalyEventForm";
import AnomalyEventSummaryContainer from "security-app/components/AnomalyEvents/AnomalyEventSummaryContainer";
import anomalyPageNavigation from "security-app/components/Layout/anomalyPageNavigation";
import assetsPageNavigation from "security-app/components/Layout/assetsPageNavigation";
import investigationsPageNavigation from "security-app/components/Layout/investigationsPageNavigation";
import SecurityRoutes from "security-app/routing/Routes";
import sigmaPageNavigation from "security-app/components/Layout/sigmaPageNavigation";
import AssetSidebarContent from "security-app/components/Assets/Sidebar/AssetSidebarContent";
import EventReplaySideBarDetails from "security-app/components/SecurityEvents/ReplaySearch/EventReplaySideBarDetails";
import InvestigationSidebarContent from "security-app/components/Investigations/Sidebar/InvestigationSidebarContent";
import investigationsPlugin from "./components/Investigations/plugin";
import assetsPlugin from "./components/Assets/plugin";
import sigmaPlugin from "./components/SigmaRules/plugin";
import eventProceduresPlugin from "./components/SecurityEvents/EventProcedures/plugin";
import securityEventsPlugin from "./components/SecurityEvents/Alerts/plugin";
import { InvestigationDrawerProvider } from "./components/Investigations/contexts";
import InvestigationDrawerRenderer from "./components/Investigations/contexts/InvestigationDrawerRenderer";
const createInvestigationNotificationDefaultConfig = {
  create_new_investigation: false,
  investigation_assigned_to: null,
  investigation_id: null,
  investigation_priority: null,
  investigation_status: null,
  type: "create-investigation-notification-v1"
};
const pluginExports = {
  entityPermissionsMapper: securityEntityPermissionsMapper,
  entityRoutes: [entityRoutesResolver],
  globalContextProviders: [InvestigationDrawerProvider],
  pageContextProviders: [InvestigationDrawerRenderer, PaginationProvider],
  eventNotificationTypes: [
    {
      type: "create-investigation-notification-v1",
      displayName: "Create Investigation Notification [Security]",
      formComponent: CreateInvestigationNotificationForm,
      summaryComponent: CreateInvestigationNotificationSummary,
      detailsComponent: CreateInvestigationNotificationDetails,
      defaultConfig: createInvestigationNotificationDefaultConfig
    }
  ],
  eventDefinitionTypes: [
    {
      type: "asset-v1",
      displayName: "Asset Events",
      sortOrder: 3,
      // Filter & Aggregation, Event Correlation, then sort alphabetically.
      description: "Create Events based on Assets.",
      formComponent: AssetEventFormContainer,
      summaryComponent: AssetEventSummaryContainer,
      defaultConfig: AssetEventForm.defaultConfig,
      useCondition: () => {
        const {
          security: { isValid: isValidSecurityLicense }
        } = useLicenseCheck();
        const { permissions } = useCurrentUser();
        return isPermitted(permissions, "asset:read") && isValidSecurityLicense;
      }
    },
    {
      type: "native-anomaly-v1",
      displayName: "Anomaly Events",
      sortOrder: 2,
      // Filter & Aggregation, Event Correlation, then sort alphabetically.
      description: "Detect anomalies using machine learning algorithms.",
      formComponent: AnomalyEventFormContainer,
      summaryComponent: AnomalyEventSummaryContainer,
      defaultConfig: AnomalyEventForm.defaultConfig,
      useCondition: () => {
        const {
          security: { isValid: isValidSecurityLicense },
          enterprise: { isValid: isValidEnterpriseLicense }
        } = useLicenseCheck();
        return isValidSecurityLicense || isValidEnterpriseLicense;
      }
    }
  ],
  fieldTypeValueRenderer: [
    {
      type: "associated-assets",
      render: (value) => /* @__PURE__ */ React.createElement(AssetsField, { value })
    }
  ],
  "events.metrics.actions": [
    { id: "export-widget", component: (props) => /* @__PURE__ */ React.createElement(ExportResultsButton, { type: "alerts", title: "alerts", ...props }) }
  ],
  pageNavigation: [anomalyPageNavigation, assetsPageNavigation, investigationsPageNavigation, sigmaPageNavigation],
  securityPage: [SecurityPage],
  entityTypeRoute: [
    {
      type: "asset",
      route: (id) => SecurityRoutes.ASSETS.details(void 0, id)
    },
    {
      type: "sigma_rule",
      // TODO: Fix details page for sigma rules
      route: (_id) => SecurityRoutes.SIGMA.LIST
    },
    {
      type: "investigation",
      route: (id) => SecurityRoutes.INVESTIGATIONS.details(id)
    }
  ],
  "sidebar.components": [
    { key: "AssetSidebarContent", component: AssetSidebarContent },
    { key: "EventReplaySideBarDetails", component: EventReplaySideBarDetails },
    { key: "InvestigationSidebarContent", component: InvestigationSidebarContent }
  ],
  ...investigationsPlugin,
  ...assetsPlugin,
  ...sigmaPlugin,
  ...eventProceduresPlugin,
  ...securityEventsPlugin
};
export default pluginExports;
